import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
    withCustomMetrics,
    INTERACTION_TYPES,
    EVENT_INITIATORS,
    MetricsClickable,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { PrimaryButton } from '@zocdoc-mezzanine/components-button';
import Textfield from '@zocdoc-mezzanine/components-textfield';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import { headingLarge } from '@zocdoc-mezzanine/foundations-typography';
import { Metrics } from './constants';
import { MFAVerificationFooter } from './MFAVerificationFooter';
import { isValidCode } from './utils/isValidCode';

/**
 *
 * @param {string} errorMessage - error message to display
 * @param {function({target: any}): void} handleChange - updates verificationCode state
 * @param {function(verificationMethod: MFA_VERIFICATION_TYPE): void} handleResendCode - handler for resending verification code
 * @param {function(*): void} handleSubmit - updates isAuthenticating to true,
 * @param {boolean} shouldDisableButton
 * @param {boolean} isCodeResent - if code was successfully resent
 * @param {boolean} isRequestingCode - is code currently being resent
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {boolean} shouldDisableForm - if true, prevents the user from entering a verification code or from requesting a new code
 * @param {string} verificationCodeDestination - obfuscated phone number
 * @param {string} verificationCode
 *
 * @returns {JSX.Element|null}
 */

const BaseMFAVerificationFormView = ({
    errorMessage,
    handleChange,
    handleResendCode,
    handleSubmit,
    shouldDisableButton,
    isCodeResent,
    isRequestingCode,
    sendMetric,
    shouldDisableForm,
    verificationCodeDestination,
    verificationCode,
}) => {
    const isButtonDisabled =
        isRequestingCode ||
        shouldDisableButton ||
        !isValidCode(verificationCode) ||
        shouldDisableForm;

    useEffect(() => {
        sendMetric({
            action: Metrics.ViewMFAForm,
            element: 'MFAVerificationFormView',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    return (
        <Wrapper data-test="verification-form-view">
            <Header data-test="verification-form-title">
                Enter the 6-digit code we sent to {verificationCodeDestination}
            </Header>
            <form method="post" onSubmit={(e) => handleSubmit(e)}>
                <InputWrapper>
                    <MetricsClickable
                        action={Metrics.ClickVerificationCode}
                        element={'VerificationCodeTextbox'}
                    >
                        <Textfield
                            id="verification-code-input"
                            name="verification-code-input"
                            dataTest="verification-form-text"
                            disabled={shouldDisableForm}
                            placeholder="Verification code"
                            labelText="Verification code"
                            onChange={handleChange}
                            invalidMessage={errorMessage}
                            value={verificationCode}
                            type="number"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                        />
                    </MetricsClickable>
                </InputWrapper>
                <PrimaryButton
                    data-test="verification-form-continue-button"
                    disabled={isButtonDisabled}
                    fluid={true}
                    type="submit"
                >
                    Continue
                </PrimaryButton>
            </form>
            {!shouldDisableForm && (
                <MFAVerificationFooter
                    isCodeResent={isCodeResent}
                    handleResendCode={handleResendCode}
                    isRequestingCode={isRequestingCode}
                />
            )}
        </Wrapper>
    );
};

export const MFAVerificationFormView = withCustomMetrics({
    section: 'MFAVerificationFormView',
    component: 'MFAVerificationFormView',
})(BaseMFAVerificationFormView);

const Wrapper = styled.div`
    color: ${blue100};

    [data-test='textfield-label'] {
        float: left;
    }

    [data-test='textfield-error'] {
        text-align: left;
    }
`;

const Header = styled.h1`
    ${headingLarge};
    margin-bottom: 24px;
    text-align: left;
`;

const InputWrapper = styled.div`
    margin: 2px 0 20px 0;
`;
